import { ListingType, SortingOption } from '@/types';

const GEO_LOCATION_ID_PATTERN = /^geo-[a-z0-9-]+$/;
const LOCATION_URL_SLUG_PATTERN = /^[a-z0-9-]+$/;

function isValidLocationSegment(value: string): boolean {
  return GEO_LOCATION_ID_PATTERN.test(value) || LOCATION_URL_SLUG_PATTERN.test(value);
}

/**
 * Validates and sanitizes a location path parameter value
 * @param value - The value to validate
 * @returns A valid location segment or undefined when invalid
 */
export function sanitizeLocationPathParam(value: unknown): string | undefined {
  if (typeof value !== 'string' || !isValidLocationSegment(value)) {
    return undefined;
  }

  return value;
}

/**
 * Validates and sanitizes a loc query parameter value
 * @param value - The value to validate
 * @returns Valid location segments; invalid segments are dropped
 */
export function sanitizeLocQueryParam(value: unknown): string[] {
  if (typeof value !== 'string') {
    return [];
  }

  return value.split(',').filter(isValidLocationSegment);
}

/**
 * Validates and sanitizes a sortBy query parameter value
 * @param value - The value to validate
 * @returns A valid SortingOption or the default value
 */
export function sanitizeSortByParam(value: unknown): SortingOption {
  // If the value is not a string, return the default
  if (typeof value !== 'string') {
    return SortingOption.DEFAULT;
  }

  // Check if the value is one of the valid enum values
  const validValues = Object.values(SortingOption);
  return validValues.includes(value as SortingOption)
    ? (value as SortingOption)
    : SortingOption.DEFAULT;
}

/**
 * Validates and sanitizes a listingType query parameter value
 * @param value - The value to validate
 * @returns A valid ListingType or the default value
 */
export function sanitizeListingTypeParam(value: unknown): ListingType {
  // If the value is not a string, return the default
  if (typeof value !== 'string') {
    return ListingType.ANY;
  }

  // Check if the value is one of the valid enum values
  const validValues = Object.values(ListingType);
  return validValues.includes(value as ListingType) ? (value as ListingType) : ListingType.ANY;
}
